// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// types
import { ErrorCause, Feedback, Ifr, IfrPayload } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';

// ui
import { Loading } from '../../ui/Loading/Loading';

// utils
import { formatDate } from '../../../utils/dates';

// props
export interface FormEditIfrProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormEditIfr = ({ onClose, onSuccess, setIsLoading }: FormEditIfrProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [ifr, setIfr] = useState<Ifr | null>(null);
	const [searchParams] = useSearchParams();
	const [values, setValues] = useState({
		dateEnd: '',
		dateStart: '',
		name: '',
	});

	// params
	const paramIfr = searchParams.get('dialogIfr');

	// context
	const { setBanner } = useContext(ContextBanner);

	// vars
	const idForm = 'form-edit-ifr';

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// fetch ifr
	const { isLoading: isLoadingIfr } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/ifrs/reports/${paramIfr}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data && res.data.length) {
				setIfr(res.data[0]);
			}
		},
	});

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		try {
			// clear feedback
			setFeedback(null);

			// set loading
			setIsLoading(true);

			if (!paramIfr) {
				throw new Error('An ID is required', {
					cause: {
						id: 'ID_NON_EXISTENT',
					},
				});
			}

			const body: IfrPayload = {
				dateEnd: String(values.dateEnd),
				dateStart: String(values.dateStart),
				name: String(values.name),
			};

			const fetchEditIfr = await fetch(
				`${process.env.REACT_APP_API_URL}/ifrs/reports/${paramIfr}`,
				{
					method: 'PATCH',
					body: JSON.stringify(body),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				}
			);
			const resEditIfr = await fetchEditIfr.json();

			if (resEditIfr.error) {
				throw new Error(resEditIfr.error.message, {
					cause: {
						id: resEditIfr.error.id,
						details: resEditIfr.error?.details,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.ifrUpdated', { name: values.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'IFR_UNAUTHORIZED_PATCH') {
					feedbackNew.alert.children = t('error.unauthorized');
				}
			}

			// set feedback
			setFeedback(feedbackNew);
		} finally {
			// set loading
			setIsLoading(false);
		}
	};

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	// content
	const renderContent = () => {
		if (isLoadingIfr) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-start-date`}
						inputProps={{
							max: values.dateEnd,
						}}
						label={t('startDate')}
						name="dateStart"
						onChange={handleChangeValue}
						required={true}
						type="date"
						value={values.dateStart}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-end-date`}
						inputProps={{
							min: values.dateStart,
						}}
						label={t('endDate')}
						name="dateEnd"
						onChange={handleChangeValue}
						required={true}
						type="date"
						value={values.dateEnd}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</>
		);
	};

	// set default values when ifr changes
	useEffect(() => {
		if (ifr) {
			setValues({
				dateEnd: formatDate({ date: new Date(ifr.dateEnd), format: 'YYYY-MM-DD' }),
				dateStart: formatDate({ date: new Date(ifr.dateStart), format: 'YYYY-MM-DD' }),
				name: ifr.name,
			});
		}
	}, [ifr]);

	return (
		<form id={`${idForm}`} name="formEditIfr" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</form>
	);
};
